import { MRT_Row, MRT_RowData } from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";

type TUseDraggableTableProps<T> = {
  initialData: T[];
  getItemId: (item: T) => string | number;
  onReorder?: (orderedIds: (string | number)[]) => Promise<void>;
};

type TUseDraggableTableReturn<T extends MRT_RowData> = {
  rows: T[];
  isSaving: boolean;
  onDragEnd: (draggedRow: MRT_Row<T>, targetRow: MRT_Row<T> | null) => void;
  setRows: React.Dispatch<React.SetStateAction<T[]>>;
};

/**
 * Hook for managing draggable table rows with immediate API persistence
 * Compatible with material-react-table row ordering
 * @param initialData - Initial array of data items
 * @param getItemId - Function to extract unique ID from each item
 * @param onReorder - Async callback to persist new order (optimistic update with rollback on error)
 */
export const useDraggableTable = <T extends MRT_RowData>({
  initialData,
  getItemId,
  onReorder,
}: TUseDraggableTableProps<T>): TUseDraggableTableReturn<T> => {
  const [rows, setRows] = useState<T[]>(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const previousRowsRef = useRef<T[]>([]);

  // Sync rows when initialData changes (e.g., after refetch)
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setRows(initialData);
    }
  }, [initialData]);

  // Handle drag end with optimistic update and rollback on error
  const onDragEnd = useCallback(
    async (draggedRow: MRT_Row<T>, targetRow: MRT_Row<T> | null) => {
      if (!targetRow || draggedRow.index === targetRow.index) {
        return;
      }

      // Save current state for potential rollback
      const previousRows = [...rows];
      previousRowsRef.current = previousRows;

      // Optimistic update - immediately reorder UI
      const reorderedRows = Array.from(rows);
      const [removed] = reorderedRows.splice(draggedRow.index, 1);
      reorderedRows.splice(targetRow.index, 0, removed);
      setRows(reorderedRows);

      // If onReorder callback provided, call API immediately
      if (onReorder) {
        setIsSaving(true);
        try {
          const orderedIds = reorderedRows.map(getItemId);
          await onReorder(orderedIds);
        } catch {
          // Rollback on error
          setRows(previousRowsRef.current);
        } finally {
          setIsSaving(false);
        }
      }
    },
    [rows, getItemId, onReorder],
  );

  return {
    rows,
    isSaving,
    onDragEnd,
    setRows,
  };
};
